/** Height classes for `TopAnnouncementBar` — keep in sync with component. */
export const TOP_ANNOUNCEMENT_BAR_HEIGHT_CLASS = "h-10 sm:h-11";

export const TOP_ANNOUNCEMENT_BAR_VISIBILITY_EVENT = "rakb:top-bar-visibility";

export const TOP_ANNOUNCEMENT_DISMISS_STORAGE_KEY = "rakb_top_announcement_dismissed_until";

export const TOP_ANNOUNCEMENT_DISMISS_DAYS = 7;

export interface TopAnnouncementVisibilityDetail {
  visible: boolean;
}

export interface TopAnnouncementVisibilityInput {
  isDismissed: boolean;
  hasSlides: boolean;
}

/** Stable visibility: hide when dismissed or when no slide can show for the current viewport. */
export const computeTopAnnouncementBarVisible = (
  input: TopAnnouncementVisibilityInput
): boolean => {
  if (input.isDismissed || !input.hasSlides) {
    return false;
  }
  return true;
};

/** While auth is loading, keep guest slides so the bar does not flicker empty. */
export const getSlidesAuthState = (
  isAuthenticated: boolean,
  authLoading: boolean
): boolean => (authLoading ? false : isAuthenticated);

export const dispatchTopAnnouncementVisibility = (visible: boolean): void => {
  if (typeof window === "undefined") {
    return;
  }
  window.dispatchEvent(
    new CustomEvent<TopAnnouncementVisibilityDetail>(TOP_ANNOUNCEMENT_BAR_VISIBILITY_EVENT, {
      detail: { visible },
    })
  );
};

/** Sync visibility for layout offsets (matches `TopAnnouncementBar` rules). */
export const readEffectiveTopAnnouncementVisible = (
  overrides: Partial<TopAnnouncementVisibilityInput> = {}
): boolean => {
  if (typeof window === "undefined") {
    return false;
  }
  return computeTopAnnouncementBarVisible({
    isDismissed: readTopAnnouncementDismissed(),
    hasSlides: overrides.hasSlides ?? true,
    ...overrides,
  });
};

export const readTopAnnouncementDismissed = (): boolean => {
  if (typeof window === "undefined") {
    return false;
  }
  try {
    const until = localStorage.getItem(TOP_ANNOUNCEMENT_DISMISS_STORAGE_KEY);
    if (!until) {
      return false;
    }
    return Date.now() < Number.parseInt(until, 10);
  } catch {
    return false;
  }
};

export const dismissTopAnnouncement = (): void => {
  if (typeof window === "undefined") {
    return;
  }
  try {
    const until = Date.now() + TOP_ANNOUNCEMENT_DISMISS_DAYS * 24 * 60 * 60 * 1000;
    localStorage.setItem(TOP_ANNOUNCEMENT_DISMISS_STORAGE_KEY, String(until));
  } catch {
    // ignore
  }
  dispatchTopAnnouncementVisibility(false);
};
